import type { WorkflowState } from '../../../store/redux/state';
import type { Change } from '../types';
import { Operation } from '../types';

/**
 * Returns a text representation of the Operation in the past tense
 * @param operation operation
 * @returns
 */
export function getOperationVerbInPastTense(operation: Operation) {
  const verb = {
    [Operation.Add]: 'added',
    [Operation.Modify]: 'modified',
    [Operation.Remove]: 'deleted',
  }[operation];

  if (!verb) {
    throw new TypeError(`The specified operation ${operation} does not exist`);
  }

  return verb;
}

/**
 * Returns the Operation contained in a Change within the `changes` array that best
 * summarizes the changes.
 * @param changes array of Change objects
 * @returns the Operation that best summarizes the list of changes
 */
export function synthesizeSingleOperationForChanges(changes: Change[]): Operation {
  const hasAddActionOperation = changes.some(({ descriptor, op }) => (
    op === Operation.Add && descriptor === 'action'));

  const hasRemoveActionOperation = changes.some(({ descriptor, op }) => (
    op === Operation.Remove && descriptor === 'action'));

  if (hasAddActionOperation) {
    return Operation.Add;
  }

  if (hasRemoveActionOperation) {
    return Operation.Remove;
  }

  return Operation.Modify;
}


export function getRelevantVersionForOp(args: {
  versions: {
    versionA: WorkflowState['workflowData'];
    versionB: WorkflowState['workflowData'];
  };
  operation: Operation;
}) {
  const { operation, versions: { versionA, versionB } } = args;
  const operationVersionMap = {
    [Operation.Add]: versionB,
    [Operation.Modify]: versionB,
    [Operation.Remove]: versionA,
  };

  return operationVersionMap[operation];
}


export function getRelevantWorkflowPropsForOp(args: {
  workflowProps: {
    propsB: WorkflowState['workflowProps'];
    propsA: WorkflowState['workflowProps'];
  };
  operation: Operation;
}) {
  const { operation, workflowProps: { propsB, propsA } } = args;
  const operationVersionMap = {
    [Operation.Add]: propsB,
    [Operation.Modify]: propsB,
    [Operation.Remove]: propsA,
  };

  return operationVersionMap[operation];
}


export function getUniqueActionPublicIds(changes: Change[]) {
  return changes.reduce((ids, change) => ids.add(change.actionPublicId), new Set<string>());
}
